import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import StarRating from './StarRating';
import { Loader2, Send, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReviewForm = ({ 
  purchaseId = null, 
  bookingId = null, 
  targetName = '',
  onSuccess = () => {},
  onCancel = () => {}
}) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      setError('Veuillez sélectionner une note');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        rating,
        comment: comment.trim() || null
      };

      if (purchaseId) {
        payload.purchase_id = purchaseId;
      } else if (bookingId) {
        payload.booking_id = bookingId;
      }

      await axios.post(`${API}/reviews`, payload, { withCredentials: true });
      onSuccess();
    } catch (err) {
      console.error('Error submitting review:', err);
      setError(err.response?.data?.detail || 'Erreur lors de l\'envoi de l\'avis');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="review-form">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Laisser un avis {targetName && `pour ${targetName}`}
        </h3>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600"
          data-testid="close-review-form"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Star Rating */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Votre note
          </label>
          <StarRating
            rating={rating}
            size="xl"
            interactive
            onChange={setRating}
          />
        </div>

        {/* Comment */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Votre commentaire (optionnel)
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Partagez votre expérience..."
            rows={4}
            maxLength={500}
            className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-300 focus:border-purple-500 outline-none resize-none"
            data-testid="review-comment"
          />
          <p className="text-xs text-gray-400 text-right mt-1">
            {comment.length}/500
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="text-red-500 text-sm bg-red-50 p-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Submit */}
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="flex-1"
          >
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={loading || rating === 0}
            className="flex-1 bg-gradient-to-r from-cyan-500 to-purple-500"
            data-testid="submit-review"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Envoyer
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ReviewForm;
